import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { getRealtimeManager } from '@talk2nick/shared';
import { Avatar } from '@talk2nick/ui';
import {
  createDefaultTranslationSettings,
  resolveMode,
  shouldTranslate,
  type Locale,
} from '@talk2nick/core';

import { requestTranslation, resolveTargetLocale } from '../features/translate/queue';
import { useTranslationUsage } from '../features/translate/usage';

import type { AuthState } from '../features/auth/useAuth';
import { useAdminClaim } from '../features/auth/useAdminClaim';
import { usePrivacy } from '../features/auth/privacy';
import { timestampMs } from '../features/chat/rooms';
import { useConvoList } from '../features/chat/useChat';
import { isMuted, useMemberships } from '../features/chat/memberships';
import { observeConvoPreviews, useUnread } from '../features/chat/unread';
import { advanceBannerWatermark } from '../features/chat/bannerWatermark';
import { useUserProfiles } from '../features/chat/useUserProfiles';
import { useIncomingInviteRequests, useMyInvitations } from '../features/chat/tickets';

/** 동시에 쌓이는 배너 수 상한 */
const MAX_BANNERS = 3;
/** 스와이프로 닫는 임계 거리 */
const DISMISS_SWIPE_PX = 60;

interface Banner {
  convoId: string;
  messageId?: string;
  text: string;
  senderId: string;
  /** 메시지 시각(ms) — 배너의 우상단 시간 */
  at: number;
  /** 같은 방에서 묶인 메시지 수 (PLAN 5-8-4장) */
  count: number;
  /** 커스텀 발신자/제목/라벨/이동경로 (초대장·초대요청·리액션용) */
  customName?: string;
  customLabel?: string;
  targetUrl?: string;
}

/**
 * 인앱 실시간 알림 + 미읽음 집계 (PLAN 5-8-4장).
 *
 * FCM OS 알림과 별개로 앱이 포그라운드일 때 띄우는 시각적 알림이다.
 * 데이터 출처는 **이미 구독 중인 대화 목록 1개**뿐이다 — 방마다 리스너를 걸지 않는다
 * (PLAN 5-8-9장 리스너 예산). 그래서 목록 미리보기(lastMessage)가 앞으로 나아간 것을
 * 감지해 배너와 미읽음 카운트를 만든다.
 *
 * 음소거(PLAN 5-8-7장)는 배너만 막고 목록/미읽음 배지는 그대로 갱신한다.
 * 서버 fanout(Functions)이 붙는 M4에서 정식 알림 판정으로 대체된다.
 */
export function NotificationHost({ auth }: { auth: AuthState }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const navigate = useNavigate();
  const location = useLocation();
  const myUid = auth.firebaseUser?.uid ?? null;
  const userEmail = auth.firebaseUser?.email?.trim().toLowerCase() ?? null;
  const isAdmin = useAdminClaim(myUid);

  const { items, loading } = useConvoList(myUid, myUid !== null);
  const memberships = useMemberships(
    myUid,
    items.map(({ id }) => id),
  );
  // 전역 번역 설정 — 알림 슬롯을 번역할지 판단한다 (PLAN 5-7-1장 표)
  const globalTranslation =
    auth.profile?.settings?.translation ?? createDefaultTranslationSettings('ko');
  // 한도 도달 시 알림 슬롯 번역도 멈춘다 (PLAN 5-7-5장)
  const translationUsage = useTranslationUsage(myUid, globalTranslation.dailyCharLimit);
  // 인앱 배너 설정은 privacy/{uid} 한 곳에서 온다 (설정 화면과 같은 구독)
  const { settings: privacy } = usePrivacy(myUid);
  const bannersEnabled = privacy.inAppBanners;
  const durationMs = privacy.inAppBannerDurationMs;
  useUnread(myUid);

  const { invitations: myInvites, loading: invitesLoading } = useMyInvitations(userEmail);
  const { requests: incomingRequests, loading: requestsLoading } =
    useIncomingInviteRequests(isAdmin);

  const [banners, setBanners] = useState<Banner[]>([]);
  const seenRef = useRef<Map<string, number>>(new Map());
  const primedRef = useRef(false);
  const seenReactionsRef = useRef<Map<string, number>>(new Map());
  const reactionsPrimedRef = useRef(false);
  const seenInvitesRef = useRef<Set<string>>(new Set());
  const invitesPrimedRef = useRef(false);
  const seenRequestsRef = useRef<Set<string>>(new Set());
  const requestsPrimedRef = useRef(false);

  // 계정이 바뀌면 이전 계정의 워터마크/프라임 상태를 초기화한다
  useEffect(() => {
    seenRef.current = new Map();
    primedRef.current = false;
    seenReactionsRef.current = new Map();
    reactionsPrimedRef.current = false;
    seenInvitesRef.current = new Set();
    invitesPrimedRef.current = false;
    seenRequestsRef.current = new Set();
    requestsPrimedRef.current = false;
    setBanners([]);
  }, [myUid]);

  // 지금 보고 있는 방은 배너를 띄우지 않는다 (PLAN 5-8-4장 조건)
  const activeConvoId = location.pathname.startsWith('/chat/')
    ? location.pathname.slice('/chat/'.length)
    : getRealtimeManager().getActiveConvoId();

  const dismiss = useCallback((convoId: string) => {
    setBanners((prev) => prev.filter((b) => b.convoId !== convoId));
  }, []);

  // 미읽음 카운트: 목록 미리보기 변화만으로 계산한다 (추가 읽기 0)
  useEffect(() => {
    if (!myUid) return;
    observeConvoPreviews(
      items.map(({ id, convo }) => ({
        convoId: id,
        at: timestampMs(convo.lastMessage?.createdAt ?? convo.updatedAt),
        senderId: convo.lastMessage?.senderId,
        lastReadAt: timestampMs(memberships.get(id)?.lastReadAt),
      })),
      myUid,
      activeConvoId,
      !loading,
    );
  }, [items, loading, myUid, activeConvoId, memberships]);

  // 삭제된 대화방의 활성 배너 즉시 닫기 (단, 가상 키 invite:/req: 배너는 유지)
  useEffect(() => {
    setBanners((prev) =>
      prev.filter(
        (b) =>
          b.convoId.startsWith('invite:') ||
          b.convoId.startsWith('req:') ||
          items.some((item) => item.id === b.convoId),
      ),
    );
  }, [items]);

  // 배너: 첫 스냅샷은 "이미 읽은 상태"로만 기록하고, 이후 증가분에만 띄운다.
  // 목록이 로드되기 전의 빈 스냅샷으로 프라임하면, 실제 목록이 도착했을 때 모든 방이
  // 처음 보는 방이 되어 이미 수신확인한 마지막 메시지가 다시 알림으로 뜬다
  // (로그인/업데이트 직후 반복 알림) — 그래서 로드된 스냅샷만 프라임 대상이다.
  useEffect(() => {
    if (!myUid) return;
    const previews = items.map(({ id, convo }) => ({
      id,
      convo,
      at: timestampMs(convo.lastMessage?.createdAt ?? convo.updatedAt),
    }));
    const previous = seenRef.current;
    const { watermark, primed, isFirstSnapshot } = advanceBannerWatermark(
      previous,
      previews.map(({ id, at }) => ({ convoId: id, at })),
      { primed: primedRef.current, loaded: !loading },
    );
    seenRef.current = watermark;
    primedRef.current = primed;
    if (isFirstSnapshot || !primed) return;

    const fresh: Banner[] = [];
    const now = Date.now();

    for (const { id, convo, at } of previews) {
      const before = previous.get(id);
      if (before !== undefined && at <= before) continue;
      const senderId = convo.lastMessage?.senderId;
      if (!senderId || senderId === myUid) continue;
      if (id === activeConvoId) continue;
      // 음소거한 방은 알림만 숨김 (PLAN 5-8-7장)
      if (isMuted(memberships.get(id), now)) continue;
      fresh.push({
        convoId: id,
        messageId: convo.lastMessage?.messageId,
        text: convo.lastMessage?.text ?? '',
        senderId,
        at,
        count: 1,
      });
    }

    if (!bannersEnabled || fresh.length === 0) return;
    // 백그라운드 탭에서는 OS 알림 경로가 담당한다 — 화면에 안 보이는 배너를 쌓지 않는다
    if (document.visibilityState !== 'visible') return;
    if (fresh.length > MAX_BANNERS) return;

    setBanners((prev) => {
      const merged = [...prev];
      for (const banner of fresh) {
        const existing = merged.findIndex((b) => b.convoId === banner.convoId);
        if (existing >= 0) {
          const current = merged[existing]!;
          merged[existing] = { ...current, ...banner, count: current.count + 1 };
        } else {
          merged.unshift(banner);
        }
      }
      return merged.slice(0, MAX_BANNERS);
    });
  }, [items, loading, myUid, activeConvoId, bannersEnabled, memberships]);

  // 리액션 실시간 배너: 상대방이 내 메시지에 리액션을 추가했을 때만 포그라운드 배너 표시 (본인이 누른 리액션 절대 제외)
  useEffect(() => {
    if (!myUid || loading) return;
    if (!reactionsPrimedRef.current) {
      const initial = new Map<string, number>();
      for (const { id, convo } of items) {
        const at = convo.lastMessage?.lastReaction?.at ?? 0;
        initial.set(id, at);
      }
      seenReactionsRef.current = initial;
      reactionsPrimedRef.current = true;
      return;
    }

    const fresh: Banner[] = [];
    const now = Date.now();
    for (const { id, convo } of items) {
      const lastReaction = convo.lastMessage?.lastReaction;
      const reactionAt = lastReaction?.at ?? 0;
      const prevAt = seenReactionsRef.current.get(id) ?? 0;
      seenReactionsRef.current.set(id, reactionAt);

      if (!lastReaction || reactionAt <= prevAt) continue;
      // 송신자(리액션 행위자) 본인에게는 절대 알림을 띄우지 않는다
      if (!lastReaction.reactorUid || lastReaction.reactorUid === myUid) continue;
      // 내 메시지에 달린 리액션일 때만 수신자에게 알린다
      const targetSenderId = lastReaction.targetSenderId ?? convo.lastMessage?.senderId;
      if (targetSenderId !== myUid) continue;
      if (id === activeConvoId) continue;
      if (isMuted(memberships.get(id), now)) continue;

      const addedEmoji = lastReaction.emoji;
      if (!addedEmoji) continue;

      fresh.push({
        convoId: id,
        messageId: lastReaction.messageId || convo.lastMessage?.messageId,
        text: `${addedEmoji} ${convo.lastMessage?.text ?? ''}`.trim(),
        senderId: lastReaction.reactorUid,
        at: reactionAt || now,
        count: 1,
        customLabel: `메시지에 ${addedEmoji} 리액션을 남겼습니다`,
      });
    }

    if (!bannersEnabled || fresh.length === 0 || document.visibilityState !== 'visible') return;
    setBanners((prev) => [...fresh, ...prev].slice(0, MAX_BANNERS));
  }, [items, loading, myUid, activeConvoId, bannersEnabled, memberships]);

  // 인앱 VIP 초대장 도착 실시간 배너 (일반 사용자)
  useEffect(() => {
    if (!myUid || invitesLoading) return;
    if (!invitesPrimedRef.current) {
      seenInvitesRef.current = new Set(myInvites.map((inv) => inv.token));
      invitesPrimedRef.current = true;
      return;
    }

    const fresh: Banner[] = [];
    const now = Date.now();
    for (const inv of myInvites) {
      if (seenInvitesRef.current.has(inv.token)) continue;
      seenInvitesRef.current.add(inv.token);
      fresh.push({
        convoId: `invite:${inv.token}`,
        text: inv.convoTitle || 'VIP 대화방 초대장',
        senderId: inv.createdBy || myUid,
        at: inv.createdAt || now,
        count: 1,
        customName: inv.createdByName || 'nIcK',
        customLabel: '✉️ VIP 초대장이 도착했습니다',
        targetUrl: '/chats?tab=invites',
      });
    }

    if (!bannersEnabled || fresh.length === 0 || document.visibilityState !== 'visible') return;
    setBanners((prev) => [...fresh, ...prev].slice(0, MAX_BANNERS));
  }, [myInvites, invitesLoading, myUid, bannersEnabled]);

  // 수신된 초대 요청 실시간 배너 (Admin 전용)
  useEffect(() => {
    if (!myUid || !isAdmin || requestsLoading) return;
    if (!requestsPrimedRef.current) {
      seenRequestsRef.current = new Set(incomingRequests.map((req) => req.id));
      requestsPrimedRef.current = true;
      return;
    }

    const fresh: Banner[] = [];
    const now = Date.now();
    for (const req of incomingRequests) {
      if (seenRequestsRef.current.has(req.id)) continue;
      seenRequestsRef.current.add(req.id);
      fresh.push({
        convoId: `req:${req.id}`,
        text: req.message || '초대 요청이 도착했습니다.',
        senderId: req.uid,
        at: req.createdAt || now,
        count: 1,
        customName: req.userName || req.userEmail,
        customLabel: '🔔 새로운 초대 요청이 도착했습니다',
        targetUrl: '/chats',
      });
    }

    if (!bannersEnabled || fresh.length === 0 || document.visibilityState !== 'visible') return;
    setBanners((prev) => [...fresh, ...prev].slice(0, MAX_BANNERS));
  }, [incomingRequests, requestsLoading, myUid, isAdmin, bannersEnabled]);

  // 배너에 필요한 발신자 프로필 — 방의 "다른 멤버"가 아니라 실제 발신자(senderId)를 쓴다
  // (그룹방에서는 다른 멤버가 여럿이라 엉뚱한 사람의 아바타/이름이 나오거나 비어 '?'가 뜬다)
  const peerUids = [
    ...new Set(banners.map((b) => b.senderId).filter((uid) => uid && uid !== myUid)),
  ];
  const profiles = useUserProfiles(peerUids);

  if (banners.length === 0) return null;

  return (
    <div className="flex w-full flex-col items-center gap-2" role="region" aria-live="polite">
      {banners.map((banner) => {
        const profile = profiles.get(banner.senderId);
        const name =
          banner.customName ??
          profile?.displayName ??
          (profile?.handle ? `@${profile.handle}` : t('chat.unknownUser', { defaultValue: '…' }));

        // always/incoming/notification 모드는 알림에서 번역문을 보여준다
        const roomMode = memberships.get(banner.convoId)?.translationMode ?? 'inherit';
        const bannerMode = resolveMode(globalTranslation.globalMode, roomMode);
        const translateTo =
          !translationUsage.reached && shouldTranslate(bannerMode, 'notification', false)
            ? resolveTargetLocale(globalTranslation.targetLang, i18n.language)
            : undefined;

        return (
          <BannerCard
            key={banner.convoId}
            banner={banner}
            name={name}
            photoURL={profile?.photoURL}
            durationMs={durationMs}
            locale={locale}
            translateTo={translateTo}
            label={
              banner.customLabel ??
              (banner.count > 1
                ? t('chat.newMessagesFrom', { name, count: banner.count })
                : t('chat.newMessage', { name }))
            }
            onDismiss={() => dismiss(banner.convoId)}
            onOpen={() => {
              dismiss(banner.convoId);
              if (banner.targetUrl) {
                navigate(banner.targetUrl);
                return;
              }
              // 해당 메시지로 스크롤 + 1.5초 하이라이트 (PLAN 5-8-4장)
              const suffix = banner.messageId ? `?m=${banner.messageId}` : '';
              navigate(`/chat/${banner.convoId}${suffix}`);
            }}
          />
        );
      })}
    </div>
  );
}

function BannerCard({
  banner,
  name,
  photoURL,
  label,
  locale,
  durationMs,
  translateTo,
  onDismiss,
  onOpen,
}: {
  banner: Banner;
  name: string;
  photoURL?: string;
  label: string;
  locale: string;
  durationMs: number;
  /** 값이 있으면 미리보기를 이 언어로 번역해 보여준다 (PLAN 5-7-1장) */
  translateTo?: Locale;
  onDismiss: () => void;
  onOpen: () => void;
}) {
  const [dragX, setDragX] = useState(0);
  const [translated, setTranslated] = useState<string | null>(null);
  const touchXRef = useRef<number | null>(null);

  // 알림 슬롯 번역 — 큐를 거치므로 같은 문구는 캐시에서 즉시 온다
  useEffect(() => {
    if (!translateTo || !banner.text) return;
    let cancelled = false;
    void requestTranslation(banner.text, translateTo)
      .then((result) => {
        if (!cancelled) setTranslated(result.text);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [banner.text, translateTo]);

  // 4초 뒤 자동 소멸 (PLAN 5-8-4장). 같은 방 메시지가 묶이면 타이머가 다시 돈다.
  useEffect(() => {
    const timer = window.setTimeout(onDismiss, durationMs);
    return () => window.clearTimeout(timer);
  }, [durationMs, onDismiss, banner.count]);

  const time = new Date(banner.at).toLocaleTimeString(locale, {
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <button
      type="button"
      onClick={onOpen}
      className="inapp-banner pointer-events-auto flex w-full max-w-[480px] items-center gap-3 rounded-[var(--radius-lg)] bg-[var(--bg-list)] px-3 py-2.5 text-left"
      style={
        dragX !== 0
          ? { transform: `translateX(${dragX}px)`, opacity: 1 - Math.abs(dragX) / 240 }
          : undefined
      }
      onTouchStart={(e) => {
        touchXRef.current = e.touches[0]?.clientX ?? null;
      }}
      onTouchMove={(e) => {
        const start = touchXRef.current;
        if (start === null) return;
        setDragX((e.touches[0]?.clientX ?? start) - start);
      }}
      onTouchEnd={() => {
        touchXRef.current = null;
        if (Math.abs(dragX) >= DISMISS_SWIPE_PX) onDismiss();
        setDragX(0);
      }}
    >
      <Avatar src={photoURL} name={name} size={40} />
      <span className="min-w-0 flex-1">
        <span className="flex items-baseline justify-between gap-2">
          <span className="truncate text-sm font-medium">{name}</span>
          <span className="shrink-0 text-xs text-[var(--text-secondary)]">{time}</span>
        </span>
        <span className="block truncate text-xs text-[var(--text-secondary)]">{label}</span>
        <span className="block truncate text-sm">{translated ?? banner.text}</span>
      </span>
    </button>
  );
}
